import { Component, OnInit, ChangeDetectorRef, OnDestroy, Output, EventEmitter } from "@angular/core";
import { Observable, Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

import { BaseControlComponent } from "../base-control/base-control.component";

import { DateService } from "../../../common/services/date.service";
import { DomainDefinitionService } from "../../../builder/services/domain-definition.service";
import { NoteService } from "../../../common/services/note.service";
import { RuleCompiler } from "../../../flow/services/rule-compiler.service";

import * as model from "../../../flow/models/flow.model";
import { convertToPlainText } from "../../../builder/models/editor.model";
import { Domain } from "../../../builder/models/domain.model";
import { Rule, SummaryControlConfig, SummaryConfig, SectionConfig, Question, QuestionKind } from "../../../common/models/flow.dto";
import { WorkOrderStatus } from "../../../common/models/work-order.model";
import { ContextInteractionService } from "../../services/context-interaction.service";


@Component({
    selector: "app-summary",
    templateUrl: "./summary.component.html",
    styleUrls: ["./summary.component.scss"],
    standalone: false
})

export class SummaryComponent extends BaseControlComponent implements OnInit, OnDestroy {
    static componentName = "SummaryComponent";

    anyPending: boolean = false;       /* set to true to see large warning */
    displaySections: SectionConfig[] = [];

    dataConfig: SummaryControlConfig;
    domains = new Map<string, Domain[]>();
    hoverTextVisible = new Map<string, boolean>();
    isPreview: boolean = false;
    loaded: boolean = false;
    questionKind = model.QuestionKind;
    noteSubscription = new Subscription();
    reviewStatus: string | null;
    sectionVisible = new Map<string, boolean>();
    @Output() summaryLoaded: EventEmitter<boolean> = new EventEmitter<boolean>();

    constructor(
        readonly dateService: DateService,
        private detector: ChangeDetectorRef,
        public readonly domainService: DomainDefinitionService,
        private noteService: NoteService,
        public readonly ruleCompiler: RuleCompiler,
        public readonly contextService: ContextInteractionService
    ) {
        super(contextService);
    }

    ngOnInit() {
        if (this.isEdit) {
            if (this.componentData.config) {
                this.loadConfig(this.componentData.config);  
            }             
        } else {
            this.detector.detectChanges();
            this.dataConfig = this.componentData.config;
            this.loadDomains();
            const noteSub$ = this.noteService.noteAdded$.pipe(finalize(() => { this.noteSubscription.add(noteSub$); })).subscribe(() => {
                this.detector.detectChanges();
            });
        }
    }

    ngOnDestroy() {
        if (this.noteSubscription) this.noteSubscription.unsubscribe();
    }

    loadConfig(config: SummaryControlConfig): SummaryControlConfig {
        this.dataConfig = config;
        if (!this.dataConfig.requiredDomains) {
            this.dataConfig.requiredDomains = [];
        }
        const sections = new Array<SectionConfig>();
        const useNames = this.dataConfig.configurations.filter(cfg => cfg.sectionId !== undefined && cfg.sectionId !== null).length === 0; //config is based on names, need to match by question

        for (let s = 0; s < this.dataConfig.sections.length; s++) {
            const section = this.dataConfig.sections[s];
            let matchingSections: SectionConfig[];
            if (useNames && section.name) {
                matchingSections = this.dataConfig.configurations.filter(cfg => cfg.sectionName === section.name);
            }
            let sectionIndex = this.dataConfig.configurations.findIndex(cfg => cfg.sectionId === section.id);
            let newSection = {
                sectionId: "",
                sectionName: "",
                summaryItems: [],
                contextItems: [],
                showHeading: false,
                displayHeading: ""
                
            } as SectionConfig;
            if (sectionIndex === -1) {
                newSection.sectionId = section.id;
                newSection.sectionName = section.name;
                newSection.displayOrder = s;
            } else {
                newSection.sectionId = this.dataConfig.configurations[sectionIndex].sectionId;
                newSection.sectionName = this.dataConfig.configurations[sectionIndex].sectionName;
                newSection.showHeading = this.dataConfig.configurations[sectionIndex].showHeading;
                newSection.displayHeading = this.dataConfig.configurations[sectionIndex].displayHeading;
                newSection.displayOrder = s;
            }           
            newSection.isRequired = section.isRequired;
            for (let p = 0; p < section.pages.length; p++) {
                let page = section.pages[p];
                for (let r = 0; r < page.questions.length; r++) {
                    let row = page.questions[r];
                    for (let q = 0; q < row.length; q++) {
                        let question = row[q];    
                        let questionConfig: any;
                        if (sectionIndex !== -1) {
                            questionConfig = this.dataConfig.configurations[sectionIndex].summaryItems.find(qu => qu.questionKey === question.key);
                        }            
                        if (questionConfig) {
                            questionConfig.type = question.type;
                            questionConfig.questionConfig = question.config;
                            questionConfig.initialDataValue =(question as Question).initialDataContextTarget; 
                            questionConfig.initialValueRule = (question as Question).initialDataContextTargetRule;
                            questionConfig.contextValue = question.contextTarget;
                            questionConfig.visibleRule = (question as Question).visibleRule;
                            questionConfig.enabledRule = (question as Question).enabledRule;
                            questionConfig.questionLabel = question.label;
                            questionConfig.summaryDisplay = (questionConfig.summaryDisplay == null || questionConfig.summaryDisplay === "undefined") ? question.label : questionConfig.summaryDisplay;
                            questionConfig.validationRules = (question as Question).validationRules;  //make sure validation rules are current even if the config already existed
                            
                            if (question.config && question.config.domain && question.config.domain !== "") {
                                questionConfig.questionDomain = question.config.domain;
                                if (this.dataConfig.requiredDomains.findIndex(i => i === questionConfig.questionDomain) === -1) {
                                    this.dataConfig.requiredDomains.push(questionConfig.questionDomain);
                                }
                            } else {
                                questionConfig.questionDomain = "";
                            }
                        } else {
                            if (useNames) {
                                for (let sct = 0; sct < matchingSections.length; sct++) {
                                    questionConfig =
                                        matchingSections[sct].summaryItems.find(qu => qu.questionKey === question.key);
                                    if (questionConfig) break;
                                }
                                if (!questionConfig) {
                                    if (question.label === "Form Actions") {
                                        if (question.type === QuestionKind.NewCasingPlan) {
                                            question.label = "Casing Plan";
                                        }
                                    }
                                    const label = convertToPlainText(question.label);
                                    questionConfig = {
                                        type: question.type,
                                        questionConfig : question.config,
                                        questionKey: question.key,
                                        questionLabel: label,
                                        isDisplayed: false,
                                        summaryDisplay: label,
                                        contextValue: question.contextTarget,
                                        initialDataValue: question.initialDataContextTarget,
                                        initialValueRule: (question as Question).initialDataContextTargetRule,
                                        visibleRule: (question as Question).visibleRule
                                    } as SummaryConfig;
                                    if (question.config && question.config.domain) {
                                        questionConfig.questionDomain = question.config.domain;
                                        if (this.dataConfig.requiredDomains.findIndex(i => i === questionConfig.questionDomain) === -1) {
                                            this.dataConfig.requiredDomains.push(questionConfig.questionDomain);
                                        }
                                    } else {
                                        questionConfig.questionDomain = "";
                                    }
                                }
                            } else {
                                const label = convertToPlainText(question.label);
                                questionConfig = {
                                    type: question.type,
                                    questionConfig: question.config,
                                    questionKey: question.key,
                                    questionLabel: label,
                                    isDisplayed: false,
                                    summaryDisplay: label,
                                    contextValue: question.contextTarget,
                                    initialDataValue : question.initialDataContextTarget,
                                    initialValueRule: (question as Question).initialDataContextTargetRule,
                                    visibleRule: (question as Question).visibleRule
                                } as SummaryConfig;
                                if (question.config && question.config.domain) {
                                    questionConfig.questionDomain = question.config.domain;
                                    if (this.dataConfig.requiredDomains.findIndex(i => i === questionConfig.questionDomain) === -1) {
                                        this.dataConfig.requiredDomains.push(questionConfig.questionDomain);
                                    }
                                } else if (question.config) {
                                    question.config.questionDomain = "";
                                }
                            }
                            questionConfig.type = question.type;
                            questionConfig.contextValue = (question.contextTarget) ? question.contextTarget : null;
                            questionConfig.validationRules = (question as Question).validationRules;
                        }
                        questionConfig.sectionNumber = s;
                        questionConfig.pageNumber = p;
                        questionConfig.rowNumber = r;
                        questionConfig.columnNumber = q;
                        this.hoverTextVisible.set(questionConfig.questionKey, false); 
                        newSection.summaryItems.push(questionConfig);
                    }
                }
            }
            this.sectionVisible.set(newSection.sectionId, true);
            sections.push(newSection);            
        }
        for (let c = 0; c < this.dataConfig.contextValues.length; c++) {
            const context = this.dataConfig.contextValues[c];
            let addContext: boolean = false;
            let addSection: boolean = false;
            let existingSection = this.dataConfig.configurations.find(conf => conf.sectionId === context.sectionId);
            if (!existingSection && useNames) {
                const matchingSections = this.dataConfig.configurations.filter(conf => conf.sectionName === context.sectionName);
                for (let m = 0; m < matchingSections.length; m++) {
                    for (let ci = 0; ci < matchingSections[m].contextItems.length; ci++) {
                        if (matchingSections[m].contextItems[ci].contextValue === context.contextTarget) {
                            existingSection = matchingSections[m];
                        }
                        if (existingSection) break;
                    }
                    if (existingSection) break;
                }
            }
            let section: SectionConfig;
            if (existingSection) {
                const isAdded = sections.find(s => s.sectionId === existingSection.sectionId);
                section = existingSection;
                if (!isAdded) {
                    addSection = true;
                }
            } else {
                section = sections.find(s => s.sectionId === context.sectionId);
                if (!section) {
                    section = {
                        sectionId: context.sectionId,
                        sectionName: context.sectionName,
                        summaryItems: [],
                        contextItems: [],
                        displayHeading: "",
                        showHeading: false
                    } as SectionConfig;
                    addSection = true;
                }
            }
            let cConfig: SummaryConfig = undefined;
            if (existingSection) {
                let existingConfig = existingSection.summaryItems.find(qc => qc.contextValue === context.contextTarget); //check to see if the context value exists as a question somewhere
                if (!existingConfig) { // not in a question, verify we haven't added the context value as a config in this section already
                    existingConfig = existingSection.contextItems.find(qc => qc.contextValue === context.contextTarget);
                }
                if (existingConfig) { //context value already exists as either a question or context value
                    cConfig = existingConfig;
                }
            }
            if (cConfig === undefined) {
                cConfig = {
                    type: model.QuestionKind.Empty,
                    questionKey: "",
                    questionLabel: "",
                    isDisplayed: false,
                    summaryDisplay: context.contextTarget,
                    contextValue: context.contextTarget
                } as SummaryConfig;
                addContext = true;
            }
            if (addContext) {
                section.contextItems.push(cConfig);
            }
            if (addSection) {
                sections.push(section);
                this.sectionVisible.set(section.sectionId, true);
            }
        }
        if (sections.length > 0) {
            for (let s = 0; s < sections.length; s++) {
                sections[s].summaryItems = sections[s].summaryItems.sort((a, b) => {
                    if (a.pageNumber < b.pageNumber) return -1;
                    if (a.pageNumber > b.pageNumber) return 1;
                    if (a.pageNumber === b.pageNumber) {
                        if (a.rowNumber < b.rowNumber) return -1;
                        if (a.rowNumber > b.rowNumber) return 1;
                        if (a.rowNumber === b.rowNumber) {
                            if (a.columnNumber < b.columnNumber) return -1;
                            if (a.columnNumber > b.columnNumber) return 1;
                            return 0;
                        }
                    }
                });
            }
            
            this.dataConfig.configurations = sections;
            this.dataConfig.sections = []; //config doesn't need to permanently store a copy of the flow's sections
        }
        return this.dataConfig;
    }

    loadDisplay(): void {
        this.reviewStatus = this.getReviewStatus();
        this.displaySections = [];
        const context = (this.flowComponent) ? this.flowComponent.workflow.context : {};
        for (let s = 0; s < this.dataConfig.configurations.length; s++) {
            const section = this.dataConfig.configurations[s];
            if (!section.isRequired) {
                section.isRequired = (): boolean => { return false; };
            }
            if (!(section.isRequired instanceof Function)) {
                section.isRequired = this.ruleCompiler.compileBooleanRule(section.isRequired as Rule, context);
            }
            if (!section.sectionId || this.sectionIsSeen(section.sectionId) || ((section.isRequired as model.RuleEvaluationFn<boolean>)())) {
                const displaySection = {
                    sectionId: section.sectionId,
                    sectionName: section.sectionName,
                    summaryItems: [],
                    contextItems: section.contextItems,
                    showHeading: section.showHeading,
                    displayHeading: section.displayHeading,
                    displayOrder: section.displayOrder,
                    isRequired: section.isRequired,
                } as SectionConfig;
                for (let q = 0; q < section.summaryItems.length; q++) {
                 
                    if (this.isPreview || section.summaryItems[q].isDisplayed) {
                        const displayQuestion = {
                            type: section.summaryItems[q].type,
                            questionKey: section.summaryItems[q].questionKey,
                            questionConfig: section.summaryItems[q].questionConfig,
                            questionLabel: section.summaryItems[q].questionLabel,
                            questionDomain: section.summaryItems[q].questionDomain,
                            isDisplayed: section.summaryItems[q].isDisplayed,
                            summaryDisplay: section.summaryItems[q].summaryDisplay,
                            contextValue: section.summaryItems[q].contextValue,
                            sectionNumber: section.summaryItems[q].sectionNumber,
                            pageNumber: section.summaryItems[q].pageNumber,
                            columnNumber: section.summaryItems[q].columnNumber,
                            rowNumber: section.summaryItems[q].rowNumber,        
                            initialDataValue: section.summaryItems[q].initialDataValue,
                            initialValueRule: section.summaryItems[q].initialValueRule,
                            validationsRules: []
                        } as SummaryConfig;

                        if (this.isPreview || section.summaryItems[q].alwaysShowInSummaries) {
                            displayQuestion.isDisplayed = true;
                        } else if (section.summaryItems[q].visibleRule) {
                            const visibleRule = section.summaryItems[q].visibleRule as Rule;
                            const compiled = (this.isPreview) ? (): boolean => { return true } : this.ruleCompiler.compileBooleanRule(visibleRule, context);
                            displayQuestion.visibleRule = compiled;
                            displayQuestion.isDisplayed = (displayQuestion.visibleRule as model.RuleEvaluationFn<boolean>)();
                        }
                        if (displayQuestion.enabledRule == null) {
                            if (section.summaryItems[q].enabledRule) {
                                if (typeof (section.summaryItems[q].enabledRule) == 'object') {
                                    const enabledRule = section.summaryItems[q].enabledRule as Rule;
                                    const compiled = (this.isPreview) ? (): boolean => { return true; } : this.ruleCompiler.compileBooleanRule(enabledRule, context);
                                    displayQuestion.enabledRule = compiled;
                                    displayQuestion.isEnabled = (displayQuestion.enabledRule as model.RuleEvaluationFn<boolean>)();
                                } else if (typeof (section.summaryItems[q].enabledRule) == 'function') {
                                    displayQuestion.isEnabled = (section.summaryItems[q].enabledRule as model.RuleEvaluationFn<boolean>)();
                                } else {
                                    displayQuestion.isEnabled = true;
                                }
                            } else {
                                displayQuestion.isEnabled = true;
                            }
                        }
                        

                        if (section.summaryItems[q].validationRules) {
                            const rules = [];
                            for (let r = 0; r < section.summaryItems[q].validationRules.length; r++) {
                                const rule = section.summaryItems[q].validationRules[r] as Rule;
                                const compiled = this.ruleCompiler.compileBooleanRule(rule, context);
                                rules.push(compiled);
                            }
                            displayQuestion.validationRules = rules;
                        }
                        displayQuestion.isValid = this.buildQuestionValidFn(displayQuestion.validationRules as model.RuleEvaluationFn<boolean>[]);
                        if (displayQuestion.isDisplayed) {
                            if (section.isRequired()) {
                                if (displayQuestion.isValid) {
                                    displayQuestion.isPending = !displayQuestion.isValid() && displayQuestion.isEnabled;
                                } else {
                                    displayQuestion.isPending = false;
                                }
                            } else {
                                displayQuestion.isPending = false;
                            }
                            displaySection.summaryItems.push(displayQuestion);
                        }

                    }
                }
                const pendingQuestions = displaySection.summaryItems.filter(si => si.isPending);
                displaySection.isPending = this.isPreview || ((displaySection.isRequired as model.RuleEvaluationFn<boolean>)() && pendingQuestions.length > 0);
                if (displaySection.summaryItems.length > 0 || displaySection.contextItems.length > 0) {
                    this.displaySections.push(displaySection);
                }
            }
        }
        const pendingSections = this.displaySections.filter(ds => ds.isPending);
        this.anyPending = pendingSections.length > 0;
        this.loaded = true;
        this.detector.detach();

        if (this.summaryLoaded != null) {
            this.summaryLoaded.emit(this.loaded);
        }        
        this.detector.detectChanges();
    }

    togglePreview(showPreview: boolean): void {
        if (showPreview) {
            this.loadDomains();
        }
        this.isPreview = showPreview;
        this.detector.detectChanges();
    }

    accordion(sectionId: string): void {
        let isVisible = this.sectionVisible.get(sectionId);
        isVisible = !isVisible;
        this.sectionVisible.set(sectionId, isVisible);
    }

    sectionIsVisible(sectionId: string): boolean {
        return this.sectionVisible.get(sectionId);
    }

    getFiles(question: SummaryConfig): any {
        let files = this.getContextData(question.contextValue);
        if (files) {
            if (question.questionConfig && question.questionConfig.useRadioButtons) {
                return files.files;
            }
            return files.value;
        }
        return [];
    }

    getQuestionValue(question: SummaryConfig, contextTarget: string): any {

        let value = "";
        let displayValue = undefined;
        if (this.componentContext && contextTarget) {
            const data = this.getContextData(contextTarget);
            if (data === undefined || data === null) return "";
            if (typeof (data) === 'object') {
                value = data.value;
                displayValue = data.displayValue;
            } else {
                value = data;
            }
            
            //convert single checkbox bools to "Yes/No"
            if (question.type === QuestionKind.Checkbox) {
                //had to use toString because using booleans worked, but the editor marked it as an error!
                switch (value.toString()) {
                    case 'true':
                        value = "Yes";
                        break;
                    case 'false':
                        value = "No";
                        break;
                    default:
                        //null catch
                        value = "";
                }
            }

            if (question.type === QuestionKind.DatePicker) {
                if (value) {
                    value = this.dateService.convertUtcStringToMMDDYYYY(value);
                } else {
                    value = 'N/A';
                }
            }

            if (value === Object(value) && question.type === QuestionKind.DataQueryButton) {
                value = "Table results cannot be displayed in Summary";
            } else if (question.questionDomain && question.questionDomain.length > 0) {
                const domainList = this.domains.get(question.questionDomain);
                if (domainList) {
                    const domain = domainList.find(d => d.Code === value);
                    if (domain)
                        value = domain.Definition;
                }
            }
        }

        if (!value && question.isPending)
            value = "Missing";
        else if (this.isPreview)
            value = question.type !== QuestionKind.Label ? "User's Answer" : "";
        else if (displayValue !== undefined)
            value = displayValue;

        return value;
    }

    getLinkText(): string {
        return (this.dataConfig.isReviewForm) ? "View" : "Edit";
    }

    navigateSection(section: SectionConfig): void {
        if (!this.isPreview && this.flowComponent !== undefined && this.flowComponent !== null) {
            const state = this.flowComponent.workflow.states.find(s => s.name === this.flowComponent.workflow.currentState);
            let sectionIndex: number;
            if (section.sectionId) {
                sectionIndex = state.sections.findIndex(s => s.id === section.sectionId);
            } else {
                sectionIndex = state.sections.findIndex(s => s.name === section.sectionName);
            }
            this.flowComponent.setPage(sectionIndex, 0);
        }
    }

    getReviewStatus(): string | null {
        if (this.flowComponent && this.flowComponent.workflow) {
            if (!this.flowComponent.workflow.currentWorkItem) {
                return null;
            }
            const workItem = this.flowComponent.workflow.associatedWorkItems.find(wi => wi.id === this.flowComponent.workflow.currentWorkItem);
            if (workItem.status === WorkOrderStatus.Completed) {
                return "Review Complete";
            }
            if (workItem.status === WorkOrderStatus.Waiting) {
                return `Review ${workItem.title} not started or returned to submitter.`;
            }
            return `Pending - ${workItem.title}`;
        }
        return null;
    }

    setHoverVisibility(questionKey: string, setting: boolean): void {
        this.hoverTextVisible.set(questionKey, setting);
    }

    getHoverVisibility(questionKey: string): boolean {
        return this.hoverTextVisible.get(questionKey);
    }

    sectionIsSeen(sectionId: string): boolean {
        if (this.isPreview) return true;
        const section = this.flowComponent.workflow.context.sections.find(s => s === sectionId);
        return (section !== undefined);
    }

    buildQuestionValidFn(ruleFns: model.RuleEvaluationFn<boolean>[]): model.RuleEvaluationFn<boolean> {
        return (): boolean => {
            let result = true;
            if (!ruleFns) return result;
            try {
                for (let r = 0; r < ruleFns.length; r++) {
                    result = ruleFns[r]();
                    if (!result) break;
                }
            } catch (error) {
                result = false;
            }

            return result;
        };
    }

    canShowNote(question: SummaryConfig): boolean {
        if (this.isPreview)
            return false;

        return this.flowComponent.canShowNote({ type: question.type, contextTarget: question.contextValue } as model.Question);
    }

    getNoteIcon(contextTarget: string): string {
        if (this.isPreview) return "";
        return this.flowComponent.getNoteIcon(contextTarget);
    }

    loadDomains(): void {
        const domainPromises: Promise<any>[] = [];
        if (this.dataConfig.requiredDomains !== undefined && this.dataConfig.requiredDomains !== null) {
            for (let d = 0; d < this.dataConfig.requiredDomains.length; d++) {
                domainPromises.push(this.getDomainData(this.dataConfig.requiredDomains[d]).toPromise());
            }
            Promise.all(domainPromises).then(data => {
                for (let p = 0; p < data.length; p++) {
                    let domainList = data[p] as Domain[];
                    if (this.domains.has(this.dataConfig.requiredDomains[p])) {
                        let list = this.domains.get(this.dataConfig.requiredDomains[p]);
                        domainList = list.concat(domainList);
                    }
                    this.domains.set(this.dataConfig.requiredDomains[p], domainList);
                }
                this.loadDisplay();
            },
                error => {
                    console.error(error);
                });
        } else {
            this.loadDisplay();
        }
    }

    addNote(event: MouseEvent, contextTarget: string): void {
        if (this.isPreview)
            return;

        event.preventDefault();
        this.flowComponent.addQuestionNote(contextTarget);
    }

    getDomainData(domain: string): Observable<any> {
        return this.domainService.getValues(domain);
    }
}
